import React, { useState, useEffect } from "react";
import {
  Page,
  Card,
  Text,
  Box,
  Button,
  Select,
  RadioButton,
  InlineStack,
} from "@shopify/polaris";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SetupTabs from "./SetupTabs";
import { useContent } from "../Provider/ContentContext";

function ContentSetup({ shopOrigin, apiKey }) {
  const navigate = useNavigate();
  const { contentData, setContentData } = useContent();

  const [language, setLanguage] = useState("en"); // default
  const [contentType, setContentType] = useState("");
  const [step, setStep] = useState(2);



  const [mobileViewHeight, setMobileViewHeight] = useState(0);

  useEffect(() => {
      const updateHeight = () => {
        if (window.innerWidth <= 768) {
          setMobileViewHeight(window.innerHeight);
        }
      };

      updateHeight();
      window.addEventListener('resize', updateHeight);
      window.addEventListener('orientationchange', updateHeight);

      return () => {
        window.removeEventListener('resize', updateHeight);
        window.removeEventListener('orientationchange', updateHeight);
      };
    }, []);

    // Then apply dynamic styles to your tone-radio div
    const mobileStyles = window.innerWidth <= 768 ? {
      position: 'sticky',
      bottom: 0,
      left: 0,
      width: '100%',
      zIndex: 1000,
      backgroundColor: '#F1F1F1',
      // boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
      padding: '15px 12px',
      margin: 0
  } : {};


  const languageOptions = [
    { label: "English (United States)", value: "en" },
    { label: "English (United Kingdom)", value: "en-GB" },
    { label: "Arabic", value: "ar" },
    { label: "Bulgarian", value: "bg" },
    { label: "Chinese", value: "zh" },
    { label: "Chinese (Simplified)", value: "zh-CN" },
    { label: "Chinese (Traditional)", value: "zh-TW" },
    { label: "Dutch", value: "nl" },
    { label: "French", value: "fr" },
    { label: "German", value: "de" },
    { label: "Indonesian", value: "id" },
    { label: "Italian", value: "it" },
    { label: "Japanese", value: "ja" },
    { label: "Korean", value: "ko" },
    { label: "Polish", value: "pl" },
    { label: "Portuguese", value: "pt" },
    { label: "Romanian", value: "ro" },
    { label: "Russian", value: "ru" },
    { label: "Spanish", value: "es" },
    { label: "Thai", value: "th" },
    { label: "Turkish", value: "tr" },
    { label: "Vietnamese", value: "vi" },
  ];

  // --- Fetch saved setup including language ---
  useEffect(() => {
    const fetchSavedSetup = async () => {
      try {
        const response = await axios.get("/api/check-setup-status/", {
          params: { shop: shopOrigin },
        });
        if (response.data.prefill) {
          const savedLanguage = response.data.prefill.language || "en";
          setLanguage(savedLanguage);

          // Optionally update context too
          setContentData((prev) => ({
            ...prev,
            language: savedLanguage,
          }));
        }
      } catch (err) {
        console.error("Failed to fetch saved setup:", err.response?.data || err.message);
      }
    };

    if (shopOrigin) fetchSavedSetup();
  }, [shopOrigin, setContentData]);

  const handleContinue = async () => {
    if (!contentType) return;

      setContentData((prev) => ({
        ...prev,
        language,
        content_type: contentType,
      }));

      const payload = {
      ...contentData,
        shop: shopOrigin || "",
        language,
        content_type: contentType,
      };

      try {
      await axios.post("/api/save-tone-content/", payload, {
          headers: { "Content-Type": "application/json" },
        });

        if (contentType === "generic") {
        navigate(`/generic-content?shop=${encodeURIComponent(shopOrigin || "")}`);
        } else if (contentType === "product") {
        navigate(`/generic-product?shop=${encodeURIComponent(shopOrigin || "")}`);
        }
      } catch (err) {
      console.error("Error saving content setup:", err.response?.data || err.message);
    }
  };

  return (
    <Page title="BlogHero Setup">
      <SetupTabs step={step} setStep={setStep} />
      <Card padding={{ xs: "600", sm: "800" }} sectioned>
        <Box paddingBlockEnd="400">
          <Text variant="headingMd" as="h2">
            Generate & edit content
          </Text>
        </Box>

        {/* <Box paddingBlockEnd="300">
          <Text variant="bodyMd" color="subdued">
            Select primary language for your content
          </Text>
          <Select
            options={languageOptions}
            value={language}
            onChange={(value) => setLanguage(value)}
          />
        </Box> */}

        <Box paddingBlockEnd="300">
          <div style={{ padding: "10px 0" }}>
            <div style={{marginBottom:"5px"}}>
            <Text variant="bodyMd" color="subdued">
              Select primary language for your content
            </Text>
            </div>
            <Select
              options={languageOptions}
              value={language}
              onChange={(value) => setLanguage(value)}
            />
          </div>

          <div
            style={{ padding: "10px 0", cursor: "pointer" }}
            onClick={() => setContentType("generic")}
          >
            <Card
              padding="400"
              sectioned
              subdued={!contentType || contentType !== "generic"}
            >
              <RadioButton
                label={
                  <Box>
                    <Text variant="bodyMd" fontWeight="medium">
                      I want to create generic content
                    </Text>
                    <Text tone="subdued">
                      Generate content on generic topics related to your buisness, such as industry trends, how-to-guides, or other general topics of interest.
                    </Text>
                  </Box>
                }
                checked={contentType === "generic"}
                id="generic"
                name="contentType"
                onChange={() => setContentType("generic")}
              />
            </Card>
          </div>

          <div
            style={{ padding: "10px 0", cursor: "pointer" }}
            onClick={() => setContentType("product")}
          >
            <Card
              padding="400"
              sectioned
              subdued={!contentType || contentType !== "product"}
            >
              <RadioButton
                label={
                  <Box>
                    <Text variant="bodyMd" fontWeight="medium">
                      I want to create product-specific content
                    </Text>
                    <Text tone="subdued">
                      Generate content on specific topics, products, features or
                      benefits, etc.
                    </Text>
                  </Box>
                }
                checked={contentType === "product"}
                id="product"
                name="contentType"
                onChange={() => setContentType("product")}
              />
            </Card>
          </div>
        </Box>
      </Card>
      <div style={{ marginTop: "15px", marginBottom: "15px", marginLeft: "24px", marginRight: "24px", ...mobileStyles }} className="tone-radio">
        <InlineStack align="space-between">
          <Button
            size="medium"
            onClick={() =>
              navigate(`/tone-content?shop=${encodeURIComponent(shopOrigin || "")}`)
            }
          >
            Back
          </Button>
          <Button
            size="medium"
            variant="primary"
            disabled={!contentType}
            onClick={handleContinue}
          >
            Continue
          </Button>
        </InlineStack>
      </div>
    </Page>
  );
}

export default ContentSetup;
